import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { OrderAPI } from '../services/api';
import AdminLogin from '../components/AdminLogin';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// Styled Components
const AdminPageContainer = styled.div`
  min-height: 100vh;
  background: #0d0d0d;
  padding: 2rem 0;
  overflow-y: auto;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  padding-bottom: 4rem;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #ffffff;
  text-align: center;
  margin-bottom: 3rem;
  background: linear-gradient(135deg, #d4af37, #f4e4bc);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 3rem;
`;

const StatCard = styled(motion.div)`
  background: #1a1a1a;
  border-radius: 12px;
  padding: 2rem;
  border: 1px solid #333333;
  text-align: center;
`;

const StatNumber = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #d4af37;
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  color: #cfcfcf;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const FiltersSection = styled.div`
  background: #1a1a1a;
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 2rem;
  border: 1px solid #333333;
`;

const FiltersTitle = styled.h3`
  color: #ffffff;
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1rem;
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid #333333;
  border-radius: 8px;
  background: #0d0d0d;
  color: #ffffff;
  font-size: 0.875rem;
  
  &:focus {
    outline: none;
    border-color: #d4af37;
  }
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #333333;
  border-radius: 8px;
  background: #0d0d0d;
  color: #ffffff;
  font-size: 0.875rem;
  
  &:focus {
    outline: none;
    border-color: #d4af37;
  }
  
  &::placeholder {
    color: #666666;
  }
`;

const OrdersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const OrderCard = styled(motion.div)`
  background: #1a1a1a;
  border-radius: 12px;
  padding: 2rem;
  border: 1px solid #333333;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #d4af37;
  }
`;

const OrderHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const OrderInfo = styled.div`
  flex: 1;
`;

const OrderId = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #d4af37;
  margin-bottom: 0.5rem;
`;

const OrderDate = styled.p`
  color: #cfcfcf;
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
`;

const OrderAmount = styled.p`
  color: #ffffff;
  font-size: 1.125rem;
  font-weight: 600;
`;

const StatusSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: flex-end;
`;

const StatusBadge = styled.div<{ status: string }>`
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  ${props => {
    switch (props.status) {
      case 'pending':
        return `
          background: #fff3cd;
          color: #856404;
        `;
      case 'confirmed':
        return `
          background: #d1ecf1;
          color: #0c5460;
        `;
      case 'shipped':
        return `
          background: #cce5ff;
          color: #004085;
        `;
      case 'delivered':
        return `
          background: #d4edda;
          color: #155724;
        `;
      case 'cancelled':
        return `
          background: #f8d7da;
          color: #721c24;
        `;
      default:
        return `
          background: #e2e3e5;
          color: #383d41;
        `;
    }
  }}
`;

const StatusSelect = styled.select`
  padding: 0.5rem;
  border: 1px solid #333333;
  border-radius: 6px;
  background: #0d0d0d;
  color: #ffffff;
  font-size: 0.875rem;
  min-width: 120px;
  
  &:focus {
    outline: none;
    border-color: #d4af37;
  }
`;

const Button = styled.button`
  padding: 0.5rem 1rem;
  background: #d4af37;
  color: #1a1a1a;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover:not(:disabled) {
    background: #b8941f;
  }
  
  &:disabled {
    background: #666666;
    cursor: not-allowed;
  }
`;

const OrderDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const DetailLabel = styled.span`
  color: #666666;
  font-size: 0.875rem;
  font-weight: 500;
`;

const DetailValue = styled.span`
  color: #ffffff;
  font-size: 0.875rem;
`;

const AdminNotesSection = styled.div`
  margin-top: 1rem;
`;

const AdminNotesLabel = styled.label`
  display: block;
  color: #d4af37;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const AdminNotesTextarea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #333333;
  border-radius: 8px;
  background: #0d0d0d;
  color: #ffffff;
  font-size: 0.875rem;
  min-height: 80px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #d4af37;
  }
  
  &::placeholder {
    color: #666666;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4rem;
  
  &::after {
    content: '';
    width: 40px;
    height: 40px;
    border: 4px solid #333333;
    border-top: 4px solid #d4af37;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  background: #ffebee;
  color: #c62828;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  text-align: center;
`;

const SuccessMessage = styled.div`
  background: #e8f5e8;
  color: #2e7d32;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  text-align: center;
`;

const LogoutButton = styled.button`
  position: fixed;
  top: 2rem;
  right: 2rem;
  padding: 0.75rem 1.5rem;
  background: transparent;
  border: 1px solid #d4af37;
  color: #d4af37;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 100;
  
  &:hover {
    background: #d4af37;
    color: #1a1a1a;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: #1a1a1a;
  border: 2px solid #d4af37;
  border-radius: 12px;
  padding: 2rem;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 10px 40px rgba(212, 175, 55, 0.3);
`;

const ModalTitle = styled.h3`
  color: #d4af37;
  margin-bottom: 1.5rem;
  font-size: 1.5rem;
  text-align: center;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  color: #ffffff;
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const ModalInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  background: #0d0d0d;
  border: 1px solid #333333;
  border-radius: 6px;
  color: #ffffff;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #d4af37;
  }
`;

const CargoSelect = styled.select`
  width: 100%;
  padding: 0.75rem;
  background: #0d0d0d;
  border: 1px solid #333333;
  border-radius: 6px;
  color: #ffffff;
  font-size: 1rem;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #d4af37;
  }
  
  option {
    background: #0d0d0d;
    color: #ffffff;
  }
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
`;

const ModalButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  flex: 1;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  ${props => props.variant === 'primary' ? `
    background: #d4af37;
    color: #1a1a1a;
    
    &:hover {
      background: #f4e4bc;
    }
  ` : `
    background: transparent;
    border: 1px solid #333333;
    color: #ffffff;
    
    &:hover {
      border-color: #d4af37;
      color: #d4af37;
    }
  `}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const QuickActionsSection = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const QuickActionButton = styled.button`
  padding: 1rem 2rem;
  background: #d4af37;
  border: none;
  border-radius: 8px;
  color: #0d0d0d;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #b8941f;
    transform: translateY(-2px);
  }
`;

const FilterButton = styled.button<{ active: boolean }>`
  padding: 0.75rem 1.5rem;
  background: ${props => props.active ? '#d4af37' : 'transparent'};
  border: 1px solid #d4af37;
  border-radius: 8px;
  color: ${props => props.active ? '#0d0d0d' : '#d4af37'};
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.active ? '#b8941f' : 'rgba(212, 175, 55, 0.1)'};
  }
`;

const QuestionCard = styled.div`
  background: #1a1a1a;
  border-radius: 12px;
  padding: 2rem;
  border: 1px solid #333333;
`;

interface ProductQuestion {
  id: number;
  product_name: string;
  customer_name: string;
  customer_email: string;
  question: string;
  answer: string | null;
  status: 'pending' | 'answered';
  created_at: string;
  answered_at: string | null;
}

const Admin: React.FC = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [activeTab, setActiveTab] = useState<'orders' | 'questions'>('orders');
  const [orders, setOrders] = useState<any[]>([]);
  
  // Product Questions state
  const [questions, setQuestions] = useState<ProductQuestion[]>([]);
  const [allQuestions, setAllQuestions] = useState<ProductQuestion[]>([]);
  const [questionFilter, setQuestionFilter] = useState<'all' | 'pending' | 'answered'>('all');
  const [selectedQuestion, setSelectedQuestion] = useState<ProductQuestion | null>(null);
  const [questionAnswer, setQuestionAnswer] = useState('');
  const [submittingAnswer, setSubmittingAnswer] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    search: ''
  });
  const [updatingOrders, setUpdatingOrders] = useState<Set<number>>(new Set());
  const [showCargoModal, setShowCargoModal] = useState(false);
  const [selectedOrderForCargo, setSelectedOrderForCargo] = useState<any>(null);
  const [cargoInfo, setCargoInfo] = useState({
    company: '',
    trackingNumber: ''
  });
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedOrderForCancel, setSelectedOrderForCancel] = useState<any>(null);
  const [cancelReason, setCancelReason] = useState('');

  useEffect(() => {
    checkAuthentication();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      loadOrders();
      fetchQuestions();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    // Filter'a göre soruları filtrele
    if (questionFilter === 'all') {
      setQuestions(allQuestions);
    } else {
      setQuestions(allQuestions.filter(q => q.status === questionFilter));
    }
  }, [questionFilter, allQuestions]);

  const checkAuthentication = async () => {
    const token = localStorage.getItem('admin_token');
    const expiresAt = localStorage.getItem('admin_expires_at');
    const adminCustomerId = localStorage.getItem('admin_customer_id');

    // Eğer token varsa ve geçerliyse, session doğrula
    if (token && expiresAt) {
      // Check if token is expired
      if (new Date(expiresAt) < new Date()) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_expires_at');
        localStorage.removeItem('admin_customer_id');
        
        // Token süresi dolmuş - admin_customer_id varsa login göster, yoksa ban
        if (!adminCustomerId) {
          // Direkt erişim - BAN
          await banAndRedirect();
          return;
        }
        
        setIsCheckingAuth(false);
        return;
      }

      // Verify session with backend
      try {
        await OrderAPI.verifyAdminSession();
        setIsAuthenticated(true);
        setIsCheckingAuth(false);
        return;
      } catch (error) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_expires_at');
        localStorage.removeItem('admin_customer_id');
        
        // Session geçersiz - admin_customer_id varsa login göster, yoksa ban
        if (!adminCustomerId) {
          await banAndRedirect();
          return;
        }
      }
    }

    // Token yok - admin müşteri kimliği kontrolü
    if (adminCustomerId) {
      // Orders sayfasından geldi, login ekranını göster
      setIsCheckingAuth(false);
      return;
    }

    // Direkt erişim denemesi - BAN!
    await banAndRedirect();
  };

  const banAndRedirect = async () => {
    // Ban isteğini gönder (arka planda)
    fetch(`${API_URL}/admin/attempt-access`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    }).catch(error => {});

    // Anında ana sayfaya yönlendir (mesaj gösterme)
    window.location.href = '/';
  };

  const handleLoginSuccess = (token: string) => {
    localStorage.removeItem('admin_customer_id');
    setIsAuthenticated(true);
  };

  const handleLogout = async () => {
    await OrderAPI.adminLogout();
    localStorage.removeItem('admin_customer_id');
    setIsAuthenticated(false);
    setOrders([]);
  };

  const loadOrders = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await OrderAPI.getAllOrders();
      // Backend { success: true, orders: [...] } formatında döndürüyor
      if (data.success && Array.isArray(data.orders)) {
        setOrders(data.orders);
      } else if (Array.isArray(data)) {
        setOrders(data);
      } else {
        setOrders([]);
      }
    } catch (err: any) {
      setError(err.message || 'Siparişler yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestions = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        return;
      }

      const response = await fetch(`${API_URL}/admin/product-questions?status=all`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (data.success && Array.isArray(data.questions)) {
        setAllQuestions(data.questions);
        setQuestions(data.questions);
      } else {
        setAllQuestions([]);
        setQuestions([]);
      }
    } catch (error) {
      setAllQuestions([]);
      setQuestions([]);
    }
  };

  const handleAnswerSubmit = async (questionId: number) => {
    if (!questionAnswer.trim()) {
      alert('Lütfen bir cevap yazın');
      return;
    }

    try {
      setSubmittingAnswer(true);
      const token = localStorage.getItem('admin_token');

      const response = await fetch(`${API_URL}/admin/product-questions/${questionId}/answer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ answer: questionAnswer })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Cevap başarıyla gönderildi!');
        setQuestionAnswer('');
        setSelectedQuestion(null);
        fetchQuestions();
      } else {
        setError(data.error || 'Cevap gönderilemedi');
      }
    } catch (error) {
      setError('Cevap gönderilirken bir hata oluştu');
    } finally {
      setSubmittingAnswer(false);
    }
  };

  const handleStatusUpdate = async (orderId: number, newStatus: string, adminNotes: string, cargoCompany?: string, cargoTrackingNumber?: string) => {
    setUpdatingOrders(prev => new Set(prev).add(orderId));
    setError('');
    setSuccess('');

    try {
      await OrderAPI.updateOrderStatus(orderId, newStatus, adminNotes, cargoCompany, cargoTrackingNumber);
      setSuccess('Sipariş durumu başarıyla güncellendi');
      
      // Update local state
      setOrders(prev => prev.map(order => 
        order.id === orderId 
          ? { 
              ...order, 
              status: newStatus, 
              admin_notes: adminNotes,
              cargo_company: cargoCompany || order.cargo_company,
              cargo_tracking_number: cargoTrackingNumber || order.cargo_tracking_number,
              updated_at: new Date().toISOString() 
            }
          : order
      ));
    } catch (error: any) {
      setError(error.message);
    } finally {
      setUpdatingOrders(prev => {
        const newSet = new Set(prev);
        newSet.delete(orderId);
        return newSet;
      });
    }
  };

  const handleCancelSubmit = async () => {
    if (!cancelReason.trim()) {
      setError('Lütfen iptal nedenini girin');
      return;
    }

    if (!selectedOrderForCancel) return;

    await handleStatusUpdate(
      selectedOrderForCancel.id, 
      'cancelled', 
      cancelReason
    );

    setShowCancelModal(false);
    setSelectedOrderForCancel(null);
    setCancelReason('');
  };

  const handleCargoSubmit = async () => {
    if (!cargoInfo.company || !cargoInfo.trackingNumber) {
      setError('Lütfen kargo şirketi ve takip numarasını girin');
      return;
    }

    if (!selectedOrderForCargo) return;

    const adminNotes = selectedOrderForCargo.admin_notes || '';
    await handleStatusUpdate(
      selectedOrderForCargo.id, 
      'shipped', 
      adminNotes,
      cargoInfo.company,
      cargoInfo.trackingNumber
    );

    setShowCargoModal(false);
    setSelectedOrderForCargo(null);
    setCargoInfo({ company: '', trackingNumber: '' });
  };

  const getStatusText = (status: string) => {
    const statusMap: { [key: string]: string } = {
      pending: 'Beklemede',
      confirmed: 'Onaylandı',
      shipped: 'Kargoya Verildi',
      delivered: 'Teslim Edildi',
      cancelled: 'İptal Edildi'
    };
    return statusMap[status] || status;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const ordersArray = Array.isArray(orders) ? orders : [];

  const filteredOrders = ordersArray.filter(order => {
    const matchesStatus = !filters.status || order.status === filters.status;
    const matchesSearch = !filters.search || 
      order.order_id.toLowerCase().includes(filters.search.toLowerCase()) ||
      order.customer_id.toLowerCase().includes(filters.search.toLowerCase()) ||
      order.email.toLowerCase().includes(filters.search.toLowerCase());
    
    return matchesStatus && matchesSearch;
  });

  const stats = {
    total: ordersArray.length,
    pending: ordersArray.filter(o => o.status === 'pending').length,
    confirmed: ordersArray.filter(o => o.status === 'confirmed').length,
    shipped: ordersArray.filter(o => o.status === 'shipped').length,
    delivered: ordersArray.filter(o => o.status === 'delivered').length,
    cancelled: ordersArray.filter(o => o.status === 'cancelled').length
  };

  // Show loading while checking authentication
  if (isCheckingAuth) {
    return (
      <AdminPageContainer>
        <LoadingSpinner />
      </AdminPageContainer>
    );
  }

  // Show login if not authenticated (sadece admin_customer_id varsa)
  if (!isAuthenticated) {
    const adminCustomerId = localStorage.getItem('admin_customer_id');
    if (adminCustomerId) {
      return <AdminLogin onLoginSuccess={handleLoginSuccess} />;
    }
    
    // admin_customer_id yoksa - anında ana sayfaya yönlendir
    window.location.href = '/';
    return null;
  }

  return (
    <AdminPageContainer>
      <Helmet>
        <title>Admin Panel - Barkın Sayın</title>
        <meta name="description" content="Sipariş yönetim paneli" />
      </Helmet>

      <LogoutButton onClick={handleLogout}>
        🚪 Çıkış Yap
      </LogoutButton>

      <Container>
        <Title>Admin Panel</Title>

        <QuickActionsSection>
          <QuickActionButton 
            onClick={() => setActiveTab('orders')}
            style={{ 
              background: activeTab === 'orders' ? '#d4af37' : 'transparent',
              color: activeTab === 'orders' ? '#0d0d0d' : '#d4af37'
            }}
          >
            📦 Siparişler
          </QuickActionButton>
          <QuickActionButton 
            onClick={() => setActiveTab('questions')}
            style={{ 
              background: activeTab === 'questions' ? '#d4af37' : 'transparent',
              color: activeTab === 'questions' ? '#0d0d0d' : '#d4af37'
            }}
          >
            ❓ Ürün Soruları
          </QuickActionButton>
        </QuickActionsSection>

        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>{success}</SuccessMessage>}

        {activeTab === 'orders' ? (
          <>
        <StatsGrid>
          <StatCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <StatNumber>{stats.total}</StatNumber>
            <StatLabel>Toplam Sipariş</StatLabel>
          </StatCard>
          <StatCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <StatNumber>{stats.pending}</StatNumber>
            <StatLabel>Beklemede</StatLabel>
          </StatCard>
          <StatCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <StatNumber>{stats.confirmed}</StatNumber>
            <StatLabel>Onaylandı</StatLabel>
          </StatCard>
          <StatCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <StatNumber>{stats.shipped}</StatNumber>
            <StatLabel>Kargoya Verildi</StatLabel>
          </StatCard>
          <StatCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <StatNumber>{stats.delivered}</StatNumber>
            <StatLabel>Teslim Edildi</StatLabel>
          </StatCard>
          <StatCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <StatNumber>{stats.cancelled}</StatNumber>
            <StatLabel>İptal Edildi</StatLabel>
          </StatCard>
        </StatsGrid>

        <FiltersSection>
          <FiltersTitle>Filtreler</FiltersTitle>
          <FiltersGrid>
            <Select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
            >
              <option value="">Tüm Durumlar</option>
              <option value="pending">Beklemede</option>
              <option value="confirmed">Onaylandı</option>
              <option value="shipped">Kargoya Verildi</option>
              <option value="delivered">Teslim Edildi</option>
              <option value="cancelled">İptal Edildi</option>
            </Select>
            <Input
              type="text"
              placeholder="Sipariş No, Müşteri Kimlik No, Ad, Email ara..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
            />
          </FiltersGrid>
        </FiltersSection>

        {loading ? (
          <LoadingSpinner />
        ) : (
          <OrdersList>
            {filteredOrders.map((order, index) => (
              <OrderCard
                key={order.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <OrderHeader>
                  <OrderInfo>
                    <OrderId>{order.order_id}</OrderId>
                    <OrderDate>{formatDate(order.created_at)}</OrderDate>
                    <OrderAmount>₺{order.total_amount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</OrderAmount>
                  </OrderInfo>
                  <StatusSection>
                    <StatusBadge status={order.status}>
                      {getStatusText(order.status)}
                    </StatusBadge>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <StatusSelect
                        value={order.status}
                        onChange={(e) => {
                          const newStatus = e.target.value;
                          
                          // Eğer "shipped" seçiliyorsa kargo modal'ını aç
                          if (newStatus === 'shipped') {
                            setSelectedOrderForCargo(order);
                            setCargoInfo({ company: '', trackingNumber: '' });
                            setShowCargoModal(true);
                          } else if (newStatus === 'cancelled') {
                            // Eğer "cancelled" seçiliyorsa iptal modal'ını aç
                            setSelectedOrderForCancel(order);
                            setCancelReason('');
                            setShowCancelModal(true);
                          } else {
                            const adminNotes = order.admin_notes || '';
                            handleStatusUpdate(order.id, newStatus, adminNotes);
                          }
                        }}
                        disabled={updatingOrders.has(order.id)}
                      >
                        <option value="pending">Beklemede</option>
                        <option value="confirmed">Onaylandı</option>
                        <option value="shipped">Kargoya Verildi</option>
                        <option value="delivered">Teslim Edildi</option>
                        <option value="cancelled">İptal Edildi</option>
                      </StatusSelect>
                      {updatingOrders.has(order.id) && (
                        <div style={{ fontSize: '0.75rem', color: '#d4af37' }}>
                          Güncelleniyor...
                        </div>
                      )}
                    </div>
                  </StatusSection>
                </OrderHeader>

                <OrderDetails>
                  <DetailItem>
                    <DetailLabel>Müşteri Kimlik No</DetailLabel>
                    <DetailValue>{order.customer_id}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Ad Soyad</DetailLabel>
                    <DetailValue>{order.first_name} {order.last_name}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Email</DetailLabel>
                    <DetailValue>{order.email}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Telefon</DetailLabel>
                    <DetailValue>{order.phone}</DetailValue>
                  </DetailItem>
                  {order.address && (
                    <DetailItem>
                      <DetailLabel>Adres</DetailLabel>
                      <DetailValue>{order.address}</DetailValue>
                    </DetailItem>
                  )}
                  {order.city && (
                    <DetailItem>
                      <DetailLabel>Şehir</DetailLabel>
                      <DetailValue>{order.city}</DetailValue>
                    </DetailItem>
                  )}
                  {order.receipt_filename && (
                    <DetailItem>
                      <DetailLabel>Dekont</DetailLabel>
                      <DetailValue>
                        <a 
                          href={`${process.env.REACT_APP_API_URL || '/api'}/download-receipt/${order.receipt_filename}`}
                          download
                          style={{
                            color: '#d4af37',
                            textDecoration: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                          }}
                        >
                          📄 {order.receipt_filename}
                          <span style={{ fontSize: '0.75rem', color: '#888' }}>(İndir)</span>
                        </a>
                      </DetailValue>
                    </DetailItem>
                  )}
                  {order.cargo_company && (
                    <DetailItem>
                      <DetailLabel>Kargo Şirketi (Müşteri Seçimi)</DetailLabel>
                      <DetailValue style={{ fontWeight: 600, color: '#4caf50' }}>
                        {order.cargo_company}
                        {order.cargo_price && ` - ₺${order.cargo_price}`}
                      </DetailValue>
                    </DetailItem>
                  )}
                  {order.cargo_tracking_number && (
                    <DetailItem>
                      <DetailLabel>Takip Numarası (Admin Girişi)</DetailLabel>
                      <DetailValue style={{ fontWeight: 600, color: '#d4af37' }}>
                        {order.cargo_tracking_number}
                      </DetailValue>
                    </DetailItem>
                  )}
                </OrderDetails>

                {/* Sipariş Edilen Ürünler */}
                {order.cart_items && order.cart_items.length > 0 ? (
                  <div style={{ 
                    marginTop: '1.5rem', 
                    padding: '1.5rem', 
                    background: 'rgba(212, 175, 55, 0.05)', 
                    borderRadius: '8px',
                    border: '1px solid rgba(212, 175, 55, 0.2)'
                  }}>
                    <h4 style={{ color: '#d4af37', marginBottom: '1rem', fontSize: '1rem', fontWeight: 600 }}>
                      📦 Sipariş Edilen Ürünler
                    </h4>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: 'rgba(212, 175, 55, 0.1)', borderBottom: '2px solid rgba(212, 175, 55, 0.3)' }}>
                          <th style={{ padding: '10px', textAlign: 'left', color: '#d4af37', fontSize: '0.875rem' }}>Ürün</th>
                          <th style={{ padding: '10px', textAlign: 'center', color: '#d4af37', fontSize: '0.875rem' }}>Adet</th>
                          <th style={{ padding: '10px', textAlign: 'right', color: '#d4af37', fontSize: '0.875rem' }}>Fiyat</th>
                          <th style={{ padding: '10px', textAlign: 'right', color: '#d4af37', fontSize: '0.875rem' }}>Toplam</th>
                        </tr>
                      </thead>
                      <tbody>
                        {order.cart_items.map((item: any, index: number) => (
                          <tr key={index} style={{ borderBottom: '1px solid rgba(212, 175, 55, 0.1)' }}>
                            <td style={{ padding: '10px', color: '#cfcfcf', fontSize: '0.875rem' }}>{item.product_name}</td>
                            <td style={{ padding: '10px', textAlign: 'center', color: '#cfcfcf', fontSize: '0.875rem' }}>{item.quantity}</td>
                            <td style={{ padding: '10px', textAlign: 'right', color: '#cfcfcf', fontSize: '0.875rem' }}>₺{item.price.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</td>
                            <td style={{ padding: '10px', textAlign: 'right', color: '#cfcfcf', fontSize: '0.875rem' }}>₺{(item.price * item.quantity).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : order.product_ids && (
                  <div style={{ 
                    marginTop: '1.5rem', 
                    padding: '1.5rem', 
                    background: 'rgba(212, 175, 55, 0.05)', 
                    borderRadius: '8px',
                    border: '1px solid rgba(212, 175, 55, 0.2)'
                  }}>
                    <h4 style={{ color: '#d4af37', marginBottom: '1rem', fontSize: '1rem', fontWeight: 600 }}>
                      📦 Sipariş Edilen Ürünler
                    </h4>
                    <div style={{ color: '#cfcfcf', fontSize: '0.875rem' }}>
                      Ürün ID'leri: {order.product_ids}
                    </div>
                  </div>
                )}

                <AdminNotesSection>
                  <AdminNotesLabel>Admin Notları</AdminNotesLabel>
                  <AdminNotesTextarea
                    value={order.admin_notes || ''}
                    onChange={(e) => {
                      const newNotes = e.target.value;
                      handleStatusUpdate(order.id, order.status, newNotes);
                    }}
                    placeholder="Müşteri için notlar ekleyin..."
                    disabled={updatingOrders.has(order.id)}
                  />
                </AdminNotesSection>
              </OrderCard>
            ))}
          </OrdersList>
        )}

        {filteredOrders.length === 0 && !loading && (
          <div style={{ textAlign: 'center', padding: '4rem', color: '#666666' }}>
            <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>📦</div>
            <h3 style={{ color: '#ffffff', marginBottom: '1rem' }}>Sipariş Bulunamadı</h3>
            <p>Filtre kriterlerinize uygun sipariş bulunamadı.</p>
          </div>
        )}

      {/* Kargo Modal */}
      {showCargoModal && (
        <ModalOverlay onClick={() => setShowCargoModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>📦 Kargo Bilgileri</ModalTitle>
            
            <FormGroup>
              <Label>Kargo Şirketi *</Label>
              <CargoSelect
                value={cargoInfo.company}
                onChange={(e) => setCargoInfo(prev => ({ ...prev, company: e.target.value }))}
              >
                <option value="">Kargo şirketi seçin</option>
                <option value="Aras Kargo">Aras Kargo (Ayakkabı: 200₺ / Giyim: 150₺)</option>
                <option value="DHL E-COMMERCE">DHL E-COMMERCE (Tüm Ürünler: 70₺)</option>
                <option value="Sürat Kargo">Sürat Kargo (Ayakkabı: 450₺ / Giyim: 350₺)</option>
              </CargoSelect>
              <div style={{ 
                marginTop: '0.5rem', 
                fontSize: '0.75rem', 
                color: '#888',
                fontStyle: 'italic'
              }}>
                💡 Kargo ücreti sepetteki ürün kategorisine göre belirlenir
              </div>
            </FormGroup>

            <FormGroup>
              <Label>Kargo Takip Numarası *</Label>
              <ModalInput
                type="text"
                placeholder="Takip numarasını girin"
                value={cargoInfo.trackingNumber}
                onChange={(e) => setCargoInfo(prev => ({ ...prev, trackingNumber: e.target.value }))}
              />
            </FormGroup>

            {error && (
              <div style={{ 
                background: '#ffebee', 
                color: '#c62828', 
                padding: '0.75rem', 
                borderRadius: '6px', 
                marginBottom: '1rem',
                fontSize: '0.875rem'
              }}>
                {error}
              </div>
            )}

            <ModalButtons>
              <ModalButton 
                variant="secondary" 
                onClick={() => {
                  setShowCargoModal(false);
                  setSelectedOrderForCargo(null);
                  setCargoInfo({ company: '', trackingNumber: '' });
                }}
              >
                İptal
              </ModalButton>
              <ModalButton 
                variant="primary" 
                onClick={handleCargoSubmit}
                disabled={!cargoInfo.company || !cargoInfo.trackingNumber}
              >
                Kaydet ve Gönder
              </ModalButton>
            </ModalButtons>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* İptal Modal */}
      {showCancelModal && (
        <ModalOverlay onClick={() => setShowCancelModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>❌ Sipariş İptali</ModalTitle>
            
            <FormGroup>
              <Label>İptal Nedeni *</Label>
              <textarea
                style={{
                  width: '100%',
                  minHeight: '120px',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }}
                placeholder="Müşteriye gönderilecek iptal nedenini yazın..."
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
              />
            </FormGroup>

            {error && (
              <div style={{ 
                background: '#ffebee', 
                color: '#c62828', 
                padding: '0.75rem', 
                borderRadius: '6px', 
                marginBottom: '1rem',
                fontSize: '0.875rem'
              }}>
                {error}
              </div>
            )}

            <ModalButtons>
              <ModalButton 
                variant="secondary" 
                onClick={() => {
                  setShowCancelModal(false);
                  setSelectedOrderForCancel(null);
                  setCancelReason('');
                }}
              >
                Vazgeç
              </ModalButton>
              <ModalButton 
                variant="primary" 
                onClick={handleCancelSubmit}
                disabled={!cancelReason.trim()}
                style={{ background: '#dc3545' }}
              >
                İptal Et
              </ModalButton>
            </ModalButtons>
          </ModalContent>
        </ModalOverlay>
      )}
        </>
        ) : (
          <div style={{ marginTop: '2rem' }}>
            <FiltersSection>
              <FilterButton 
                active={questionFilter === 'all'} 
                onClick={() => setQuestionFilter('all')}
              >
                Tümü ({allQuestions.length})
              </FilterButton>
              <FilterButton 
                active={questionFilter === 'pending'} 
                onClick={() => setQuestionFilter('pending')}
              >
                Bekleyenler ({allQuestions.filter(q => q.status === 'pending').length})
              </FilterButton>
              <FilterButton 
                active={questionFilter === 'answered'} 
                onClick={() => setQuestionFilter('answered')}
              >
                Cevaplananlar ({allQuestions.filter(q => q.status === 'answered').length})
              </FilterButton>
            </FiltersSection>

            {questions.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '4rem', color: '#666666' }}>
                <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>❓</div>
                <h3 style={{ color: '#ffffff', marginBottom: '1rem' }}>Soru Bulunamadı</h3>
                <p>Henüz ürün sorusu bulunmuyor.</p>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '1.5rem' }}>
                {questions.map((question) => (
                  <QuestionCard key={question.id}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #333' }}>
                      <h3 style={{ color: '#ffffff', fontSize: '1.25rem', fontWeight: 600 }}>{question.product_name}</h3>
                      <span style={{ 
                        padding: '0.5rem 1rem', 
                        borderRadius: '20px', 
                        fontSize: '0.875rem', 
                        fontWeight: 600,
                        background: question.status === 'pending' ? '#ff9800' : '#4caf50',
                        color: '#ffffff'
                      }}>
                        {question.status === 'pending' ? 'Bekliyor' : 'Cevaplandı'}
                      </span>
                    </div>

                    <div style={{ marginBottom: '1rem' }}>
                      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <span style={{ color: '#888', fontSize: '0.875rem', minWidth: '80px' }}>Müşteri:</span>
                        <span style={{ color: '#cfcfcf', fontSize: '0.875rem' }}>{question.customer_name}</span>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <span style={{ color: '#888', fontSize: '0.875rem', minWidth: '80px' }}>Email:</span>
                        <span style={{ color: '#cfcfcf', fontSize: '0.875rem' }}>{question.customer_email}</span>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <span style={{ color: '#888', fontSize: '0.875rem', minWidth: '80px' }}>Tarih:</span>
                        <span style={{ color: '#cfcfcf', fontSize: '0.875rem' }}>{new Date(question.created_at).toLocaleString('tr-TR')}</span>
                      </div>
                    </div>

                    <div style={{ marginBottom: '1.5rem', padding: '1rem', background: 'rgba(212, 175, 55, 0.05)', borderLeft: '4px solid #d4af37', borderRadius: '4px' }}>
                      <div style={{ color: '#d4af37', fontSize: '0.875rem', fontWeight: 600, textTransform: 'uppercase', marginBottom: '0.5rem' }}>Soru:</div>
                      <p style={{ color: '#ffffff', fontSize: '1rem', lineHeight: 1.6, whiteSpace: 'pre-wrap', margin: 0 }}>{question.question}</p>
                    </div>

                    {question.answer && (
                      <div style={{ marginBottom: '1.5rem', padding: '1rem', background: 'rgba(76, 175, 80, 0.05)', borderLeft: '4px solid #4caf50', borderRadius: '4px' }}>
                        <div style={{ color: '#4caf50', fontSize: '0.875rem', fontWeight: 600, textTransform: 'uppercase', marginBottom: '0.5rem' }}>Cevap:</div>
                        <p style={{ color: '#ffffff', fontSize: '1rem', lineHeight: 1.6, whiteSpace: 'pre-wrap', margin: 0 }}>{question.answer}</p>
                        <div style={{ color: '#888', fontSize: '0.75rem', marginTop: '0.5rem' }}>
                          Cevaplandı: {question.answered_at && new Date(question.answered_at).toLocaleString('tr-TR')}
                        </div>
                      </div>
                    )}

                    {question.status === 'pending' && (
                      <>
                        {selectedQuestion?.id === question.id ? (
                          <div style={{ marginTop: '1.5rem' }}>
                            <AdminNotesTextarea
                              value={questionAnswer}
                              onChange={(e) => setQuestionAnswer(e.target.value)}
                              placeholder="Cevabınızı yazın..."
                              rows={5}
                            />
                            <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                              <button 
                                onClick={() => handleAnswerSubmit(question.id)}
                                disabled={submittingAnswer}
                                style={{
                                  padding: '0.75rem 2rem',
                                  background: '#4caf50',
                                  border: 'none',
                                  borderRadius: '8px',
                                  color: '#ffffff',
                                  fontSize: '1rem',
                                  fontWeight: 600,
                                  cursor: submittingAnswer ? 'not-allowed' : 'pointer',
                                  opacity: submittingAnswer ? 0.6 : 1
                                }}
                              >
                                {submittingAnswer ? 'Gönderiliyor...' : 'Cevabı Gönder'}
                              </button>
                              <button 
                                onClick={() => {
                                  setSelectedQuestion(null);
                                  setQuestionAnswer('');
                                }}
                                style={{
                                  padding: '0.75rem 2rem',
                                  background: 'transparent',
                                  border: '1px solid #888',
                                  borderRadius: '8px',
                                  color: '#888',
                                  fontSize: '1rem',
                                  fontWeight: 600,
                                  cursor: 'pointer'
                                }}
                              >
                                İptal
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button 
                            onClick={() => setSelectedQuestion(question)}
                            style={{
                              padding: '0.75rem 2rem',
                              background: '#d4af37',
                              border: 'none',
                              borderRadius: '8px',
                              color: '#0d0d0d',
                              fontSize: '1rem',
                              fontWeight: 600,
                              cursor: 'pointer'
                            }}
                          >
                            Cevapla
                          </button>
                        )}
                      </>
                    )}
                  </QuestionCard>
                ))}
              </div>
            )}
          </div>
        )}
      </Container>
    </AdminPageContainer>
  );
};

export default Admin;
